import { Badge, Box, Group, Text } from '@mantine/core';
import { useInvoiceSelection } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/use-invoice-selection';
import dayjs from 'dayjs';
import { tKeys, useTranslation } from 'locales';
import type {
  InvoiceStatusKey,
  UnhandledInvoice,
} from 'modules/AtRisk/api/use-load-unhandled-client';
import { isDefined, isEmpty } from 'remeda';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import { highlightedCardClass } from './InvoiceCard.styles';

type InvoiceCardProps = { invoice: UnhandledInvoice; hideBorder?: boolean };

export const InvoiceCard = ({ invoice, hideBorder = false }: InvoiceCardProps) => {
  const { t } = useTranslation();
  const formatter = useLocalizedCurrencyFormatter();

  const { isInvoiceHighlighted } = useInvoiceSelection();

  const daysLate = dayjs().diff(invoice.dueDate, 'day');

  return (
    <Box
      px={8}
      py={6}
      className={highlightedCardClass}
      aria-current={isInvoiceHighlighted(invoice.id) || undefined}
      style={hideBorder ? { borderBottom: 'none' } : undefined}
    >
      <Group w="100%" justify="space-between">
        <Group gap="xs">
          <Text size="xs" fw="bold">
            {invoice.reference}
          </Text>
          •
          <Text size="xs" c="dimmed">
            {t(tKeys.DAYS_LATE, { count: daysLate })}
          </Text>
          {isDefined(invoice.status) && (
            <Badge
              px={6}
              bdrs={6}
              variant="light"
              color={statusPillColor(invoice.statusKey)}
              size="xs"
            >
              {withoutLatenessPrefix(invoice.status)}
            </Badge>
          )}
        </Group>
        <Text size="xs" fw="bold">
          {formatter.format(invoice.totalTvac)}
        </Text>
      </Group>
    </Box>
  );
};

const withoutLatenessPrefix = (status: string): string => {
  const [, ...detail] = status.split(' - ');
  return isEmpty(detail) ? status : detail.join(' - ');
};

const NEUTRAL_PILL_COLOR = 'gray';

const STATUS_PILL_COLORS: Partial<Record<InvoiceStatusKey, string>> = {
  last_reminder_sent: 'orange',
  formal_notice_sent: 'red',
};

const statusPillColor = (statusKey: InvoiceStatusKey | undefined): string =>
  isDefined(statusKey) ? (STATUS_PILL_COLORS[statusKey] ?? NEUTRAL_PILL_COLOR) : NEUTRAL_PILL_COLOR;
